import { useState } from 'react';
import { Button, Segmented } from 'antd';

import EventFormElement from '../events/EventFormElement';
import DateField from '../common/DateField';
import { RelativeDateComposer } from './DateOptions';
import { describeRelativeBound } from '../../utils/dateBounds';
import {
	clockDefault,
	currencyDefaultToInput,
	currencyInputToDefault,
	explicitDefault,
	storedDefaultProblem,
	takesDefault,
} from '../../utils/elementDefaults';

/**
 * DefaultValueField — an element's Default value (docs/plans/data-types-second-
 * pass.md §3.6, PR 4; the owner's rulings D7 and D8): the value a new item
 * starts with, which the API applies on every create path that leaves the
 * element out (keepr-api utils/elementDefaults.js) and the forms seed.
 *
 * THE FORM A PERSON FILLS IS THE FORM THE DEFAULT IS SET IN. The field renders
 * the element's own input (the item form's dispatch, EventFormElement, over the
 * Input.js inputs) with the element's own options — a choice as its choices, a
 * rating as its stars, money as its amount-and-currency control, a phone
 * against its country — so the default is entered, and checked, exactly as a
 * value would be. Two types need more than their input:
 *
 *   date, date-time   None · A date · Relative to today — a literal in the
 *                     platform's date field, or the relative composer the
 *                     Dates panel's bounds use ("30 days after today").
 *                     "Default to today" / "Default to now" (toggle rows) win
 *                     when both are set, and the field says so.
 *   currency          stored { value, currency } in MAJOR units; the control
 *                     holds { amount, currency } in minor units.
 *
 * A short text is offered a text default whatever list it carries: choices
 * live only on choice elements, and a list left on a short text is inert
 * (KPE-033; the "Make it a Choice" nudge that stood here went with KPR-1015).
 *
 * The API's refusal (400 invalid_options { option: 'defaultValue' }) lands here
 * (`error`), and so does the same question asked in the browser
 * (storedDefaultProblem): a default a later rule leaves outside — a removed
 * choice, a tightened maximum length or bound, a pattern — or one beside a
 * driven, unique or numbered element. "Clear default" is always one click.
 *
 * Props: `element` (as edited: dataType, options, name), `idPrefix` (the
 * options path, for ids), `onPatch(partial options)` (undefined clears a key),
 * `error`, `compact` (the multi add editor's ad hoc inputs).
 */

const LABEL = 'Default value (new items)';
// Option keys that make no sense on the input a DEFAULT is typed into: it is
// never required, never driven or numbered, never scanned, and carries no
// default of its own.
const INPUT_STRIP = ['required', 'requiredWhen', 'help', 'drivenFrom', 'sequence', 'unique', 'defaultValue', 'defaultToToday', 'defaultToNow', 'scan', 'group', 'isTitle'];

function inputElementFor(element, name) {
	const options = { ...(element?.options || {}) };
	INPUT_STRIP.forEach((key) => { delete options[key]; });
	return { name, dataType: element?.dataType, label: { singular: LABEL, plural: LABEL }, options };
}

/** What the input shows for the stored default. */
function inputValueOf(element, raw) {
	const dataType = element?.dataType;
	if (dataType === 'currency') return currencyDefaultToInput(raw, element?.options);
	if (dataType === 'boolean') return raw === true;
	if (raw === undefined) return ['text-small', 'text-large', 'phone', 'email', 'url'].includes(dataType) ? '' : null;
	return raw;
}

/** What the input's value stores as the default (undefined clears it). */
function defaultOf(element, value) {
	const dataType = element?.dataType;
	if (dataType === 'currency') return currencyInputToDefault(value);
	// A switch has two states; "Yes" is the default worth storing — off is
	// where every new yes/no starts anyway.
	if (dataType === 'boolean') return value === true ? true : undefined;
	if (['number', 'decimal', 'integer', 'rating'].includes(dataType)) {
		// A rating's 0 is "no stars" — no default.
		if (dataType === 'rating' && value === 0) return undefined;
		return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
	}
	if (value === null || value === undefined || value === '') return undefined;
	return value;
}

const DATE_KINDS = dataType => [
	{ value: 'none', label: 'None' },
	{ value: 'literal', label: dataType === 'date-time' ? 'A date and time' : 'A date' },
	{ value: 'relative', label: 'Relative to today' },
];

export default function DefaultValueField({ element, idPrefix, onPatch, error = null, compact = false }) {
	const dataType = element?.dataType;
	const options = element?.options || {};
	const raw = explicitDefault(element);
	const isSet = raw !== undefined;
	// The kind being AUTHORED on a date: the stored default speaks for itself;
	// the local value only carries the gap between choosing "A date" and
	// picking one (a half-written default is never stored).
	const [pendingKind, setPendingKind] = useState(null);

	if (!takesDefault(dataType)) return null;
	const name = `${idPrefix}.defaultValue`;
	const clear = () => { setPendingKind(null); onPatch({ defaultValue: undefined }); };
	const problem = (error && error.message ? String(error.message).replace(/^elements\.[^:]+:\s*/, '') : null) || storedDefaultProblem(element);

	const clearButton = isSet ? (
		<Button type='link' size='small' className='default-value-field__clear' onClick={clear}>
			Clear default
		</Button>
	) : null;
	const head = (
		<div className='default-value-field__head'>
			<label className='d-block' htmlFor={name}>{LABEL}</label>
			{clearButton}
		</div>
	);
	const problemLine = problem ? <div className='element-field__error' role='alert'>{problem}</div> : null;

	const clock = clockDefault(element);
	if (clock) {
		const toggle = clock === 'today' ? 'Default to today' : 'Default to now';
		const when = clock === 'today' ? 'today' : (dataType === 'time' ? 'at the current time' : 'now');
		return (
			<div className='default-value-field' data-testid='default-value-field'>
				{head}
				<div className='form-panel__hint'>
					&ldquo;{toggle}&rdquo; is on, so new items start {when}{isSet ? ' — it wins over the default set here.' : '.'}
				</div>
				{problemLine}
			</div>
		);
	}

	if (dataType === 'date' || dataType === 'date-time') {
		const storedKind = raw && typeof raw === 'object' && (raw.kind === 'literal' || raw.kind === 'relative') ? raw.kind : null;
		const kind = storedKind || pendingKind || 'none';
		const setKind = (next) => {
			setPendingKind(next);
			if (next === 'none') onPatch({ defaultValue: undefined });
			// "Today" is complete the moment it is chosen — the commonest
			// relative default there is, not a placeholder.
			else if (next === 'relative') onPatch({ defaultValue: { kind: 'relative', value: 'today' } });
			else if (next !== storedKind) onPatch({ defaultValue: undefined });
		};
		return (
			<div className='default-value-field' data-testid='default-value-field'>
				{head}
				<Segmented
					id={name}
					aria-label={LABEL}
					size={compact ? 'small' : 'middle'}
					value={kind}
					onChange={setKind}
					options={DATE_KINDS(dataType)}
				/>
				{kind === 'literal' ? (
					<div className='date-options__bound-body'>
						<DateField
							mode={dataType}
							elementName={`${name}.value`}
							elementLabel='Default date'
							value={storedKind === 'literal' ? raw.value : null}
							onChange={(_, value) => onPatch({ defaultValue: value ? { kind: 'literal', value } : undefined })}
						/>
					</div>
				) : null}
				{kind === 'relative' ? (
					<>
						<RelativeDateComposer
							dataType={dataType}
							label='Default'
							size={compact ? 'small' : 'middle'}
							value={storedKind === 'relative' ? raw.value : 'today'}
							onChange={value => onPatch({ defaultValue: { kind: 'relative', value } })}
						/>
						{storedKind === 'relative' ? (
							<div className='form-panel__hint'>New items start {describeRelativeBound(raw.value)}.</div>
						) : null}
					</>
				) : null}
				{problemLine}
			</div>
		);
	}

	const inputElement = inputElementFor(element, name);
	return (
		<div className='default-value-field' data-testid='default-value-field'>
			{clearButton ? <div className='default-value-field__head default-value-field__head--end'>{clearButton}</div> : null}
			<EventFormElement
				event={{ elements: {} }}
				element={inputElement}
				value={inputValueOf(element, raw)}
				onChange={(_, value) => onPatch({ defaultValue: defaultOf(element, value) })}
				cardMeta={null}
				showIconBadge={false}
				errorMessage={problem}
				siblingDefs={[inputElement]}
			/>
			{dataType === 'boolean' && !isSet ? (
				<div className='form-panel__hint'>Off: new items start at {options.falseLabel || 'No'}.</div>
			) : null}
		</div>
	);
}
